import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { useLocalTime } from "../../../../utils/timestamp";

import { scanHealthCheckKeys } from "../../../../api/queryKeys";
import IconButton from "../../../../components/buttons/IconButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { getPrettyDate } from "../../../../utils/timestamp";
import { icons } from "../../../stack/environment/icons/iconExport";
import EntitiesHealthCheck from "./entitiesHealthCheck/EntitiesHealthCheckTable";
import VulnerabilitiesHealthCheckTable from "./vulnerabilitiesHealthCheck/VulnerabilitiesHealthCheckTable";

export default function HealthCheckView() {
  const localTime = useLocalTime();
  const { getScanHealthChecks } = useAPI();
  const [selectedTimestampIndex, setSelectedTimestampIndex] = useState(null);

  const healthCheckQuery = useQuery({
    queryKey: scanHealthCheckKeys.all,
    queryFn: getScanHealthChecks,
    select: (response) => response.data,
  });

  let dataByTimestamp = healthCheckQuery.isSuccess
    ? healthCheckQuery.data.reduce((accumulator, currentValue) => {
        accumulator[currentValue.timestamp] = currentValue;
        return accumulator;
      }, {})
    : null;

  // newest timestamp first
  const orderedTimestamps = dataByTimestamp
    ? Object.keys(dataByTimestamp).sort((a, b) => b - a)
    : null;

  if (
    healthCheckQuery.isSuccess &&
    healthCheckQuery.data.length > 0 &&
    selectedTimestampIndex === null
  ) {
    setSelectedTimestampIndex(0);
  }

  const selectedTimestamp =
    selectedTimestampIndex !== null
      ? orderedTimestamps[selectedTimestampIndex]
      : null;

  const handlePreviousTimestamp = () => {
    if (selectedTimestampIndex < orderedTimestamps.length - 1) {
      setSelectedTimestampIndex(selectedTimestampIndex + 1);
    }
  };

  const handleNextTimestamp = () => {
    if (selectedTimestampIndex > 0) {
      setSelectedTimestampIndex(selectedTimestampIndex - 1);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} sx={{ ml: "16px" }}>
      <Typography variant="subtitle1">Health Check</Typography>
      {healthCheckQuery.isLoading ? (
        <LoadingIcon />
      ) : healthCheckQuery.isSuccess && healthCheckQuery.data.length === 0 ? (
        <Box>
          <Typography variant="subtitle2">
            No data is currently available for this page
          </Typography>
        </Box>
      ) : healthCheckQuery.isSuccess && selectedTimestamp ? (
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          alignItems="center"
          width="fit-content"
        >
          <Box display="flex" flexDirection="row" gap={1} alignItems="center">
            <IconButton
              onClick={handlePreviousTimestamp}
              IconSVG={icons.chevronLeftIcon}
              size="M"
              buttonStyle="standard"
              disabled={selectedTimestampIndex === orderedTimestamps.length - 1}
            />
            <Typography variant="subtitle1">
              {getPrettyDate(selectedTimestamp, localTime)}
            </Typography>
            <IconButton
              onClick={handleNextTimestamp}
              IconSVG={icons.chevronRightIcon}
              size="M"
              buttonStyle="standard"
              disabled={selectedTimestampIndex === 0}
            />
          </Box>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="subtitle2">Entities</Typography>
            <EntitiesHealthCheck
              dataByTimestamp={dataByTimestamp}
              selectedTimestampIndex={selectedTimestampIndex}
              orderedTimestamps={orderedTimestamps}
            />
            <Typography variant="subtitle2">Vulnerabilities</Typography>
            <VulnerabilitiesHealthCheckTable
              dataByTimestamp={dataByTimestamp}
              selectedTimestampIndex={selectedTimestampIndex}
              orderedTimestamps={orderedTimestamps}
            />
          </Box>
        </Box>
      ) : (
        <Box>
          <Typography
            variant="subtitle2"
            align="center"
            sx={{ paddingTop: "20px" }}
          >
            This page is currently unavailable
          </Typography>
        </Box>
      )}
    </Box>
  );
}
